import { useState } from 'react'
import { useModules, useSetModule } from '../../api/queries'
import type { ModuleState } from '../../api/types'
import { useAuth } from '../../auth/AuthProvider'
import { Card, ErrorBox, Loading } from '../../components/ui'

/**
 * Tænd og sluk for systemets moduler (beslutning 048).
 *
 * Kernen - sager, opgaver, kalender, kunder og brugere - står ikke på listen.
 * Den kan ikke slås fra, for uden den er der ingenting tilbage at logge ind på.
 *
 * Det er ikke bare menuen, der skjules: et slukket modul svarer heller ikke på
 * sine adresser, og dets baggrundsjob holder op med at køre. Det står der, fordi
 * det er selve pointen - ellers ville systemet blive ved med at sende varsler og
 * slå adresser op for noget, huset har slukket.
 *
 * Intranettets funktioner (beslutning 119) står under Intranet og kan kun
 * tændes og slukkes af Superadmin. Slukkes Intranet, er de alle fra, men deres
 * egen knap huskes til næste gang.
 */
export function ModulesPage() {
  const { data, isLoading, error } = useModules()
  const setModule = useSetModule()
  const { isSuperadmin } = useAuth()
  const [failed, setFailed] = useState<string | null>(null)

  // Hvilket modul er midt i at blive skiftet - så knappen kan vise det.
  const [pending, setPending] = useState<string | null>(null)

  async function toggle(module: ModuleState) {
    setFailed(null)
    setPending(module.key)
    try {
      await setModule.mutateAsync({ key: module.key, enabled: !(module.enabledSelf ?? module.enabled) })
    } catch (e) {
      setFailed(e instanceof Error ? e.message : 'Modulet kunne ikke ændres.')
    } finally {
      setPending(null)
    }
  }

  if (isLoading) return <Loading />
  if (error) return <ErrorBox error={error} />

  const off = data?.filter((m) => !m.parent && !m.enabled).length ?? 0

  // Ét modul med dets dele under sig (og delenes dele).
  const renderModule = (module: ModuleState, depth: number): JSX.Element => {
    const self = module.enabledSelf ?? module.enabled
    const locked = module.superadminOnly === true && !isSuperadmin
    const parentName = data?.find((m) => m.key === module.parent)?.name
    const children = data?.filter((m) => m.parent === module.key) ?? []

    return (
      <div key={module.key}>
        <div
          className={`module ${module.enabled ? '' : 'module--off'}`}
          style={depth > 0 ? { paddingLeft: depth * 22 } : undefined}
        >
          <div className="module__text">
            <div className="module__name">
              {depth > 0 && <span className="muted">↳</span>}
              {module.name}
              {!self && <span className="module__badge">Slået fra</span>}
              {self && !module.enabled && parentName && (
                <span className="module__badge">Fra, fordi {parentName} er slået fra</span>
              )}
            </div>
            <div className="tiny muted">{module.description}</div>
          </div>
          <button
            className={`btn btn--sm ${self ? '' : 'btn--primary'}`}
            disabled={pending !== null || locked}
            title={locked ? 'Kun Superadmin kan tænde og slukke for intranettets funktioner.' : undefined}
            onClick={() => toggle(module)}
          >
            {pending === module.key ? 'Gemmer ...' : self ? 'Slå fra' : 'Slå til'}
          </button>
        </div>
        {children.length > 0 && (
          <>
            {depth === 0 && (
              <div className="tiny muted" style={{ padding: '8px 0 0 22px' }}>
                Funktionerne i {module.name}{children.some((c) => c.superadminOnly) ? ' - kun Superadmin kan tænde og slukke' : ''}:
              </div>
            )}
            {children.map((child) => renderModule(child, depth + 1))}
          </>
        )}
      </div>
    )
  }

  return (
    <Card title="Moduler">
      <p className="small muted" style={{ marginTop: 0 }}>
        Slå de dele fra, I ikke bruger. Et slukket modul forsvinder fra menuen, svarer ikke
        længere på sine adresser, og holder op med at køre sine baggrundsjob - så systemet ikke
        bliver ved med at sende varsler om noget, I har slået fra. Data slettes ikke: tændes
        modulet igen, er alt, som det var.
      </p>
      <p className="small muted">
        Sager, opgaver, kalender, kunder og brugere kan ikke slås fra - det er det, systemet er.
      </p>

      {failed && <div className="error-box" style={{ marginBottom: 12 }}>{failed}</div>}

      <div className="module-list">
        {data?.filter((m) => !m.parent).map((module) => renderModule(module, 0))}
      </div>

      {off > 0 && (
        <p className="tiny muted" style={{ marginTop: 12 }}>
          {off === 1 ? 'Ét modul er slået fra' : `${off} moduler er slået fra`}. Ændringen står i
          aktivitetsloggen, så man senere kan se, hvem der slukkede for hvad.
        </p>
      )}
    </Card>
  )
}
